import { faHourglassHalf } from "@fortawesome/duotone-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { JoonUIColor, Switch, Typography } from "@joonapp/web-shared"
import dayjs from "dayjs"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { useScreenTimeRestrictionsModalStore } from "./ScreenTimeRestrictionsModal"
import { useToggleScreenTimeRestrictionsModalStore } from "./ToggleScreenTimeRestrictionsModal"
import SettingsGearButton from "../../../components/buttons/SettingsGearButton"
import { DAY_INDEX_MAP } from "../../../constants"
import { useFamilyQuery } from "../../../networking/queries"
import {
  useScreenTimeManualExceptionsQuery,
  useScreenTimeScheduledExceptionsQuery,
} from "../../../networking/rewards/queries"
import { FamilyQuerySelect } from "../../../types"
import useRewardsUserSelectStore from "../hooks/useRewardsUserSelectStore"

const WEEKDAY_ABBR_KEYS = {
  monday: "DAY_ABBR_MON",
  tuesday: "DAY_ABBR_TUE",
  wednesday: "DAY_ABBR_WED",
  thursday: "DAY_ABBR_THU",
  friday: "DAY_ABBR_FRI",
  saturday: "DAY_ABBR_SAT",
  sunday: "DAY_ABBR_SUN",
} as const

const ScreenTimeRestrictionsSection = () => {
  const [isHovered, setIsHovered] = useState(false)
  const { selectedUserId } = useRewardsUserSelectStore()
  const { t } = useTranslation()
  const { data: children } = useFamilyQuery(FamilyQuerySelect.CHILDREN)
  const child = children?.find((child) => child.user.id === selectedUserId)
  const { onOpen: onOpenToggleScreenTimeRestrictionsModal } =
    useToggleScreenTimeRestrictionsModalStore()
  const { onOpen: onOpenScreenTimeRestrictionsModal } =
    useScreenTimeRestrictionsModalStore()
  const { data: scheduledExceptions } = useScreenTimeScheduledExceptionsQuery({
    userId: selectedUserId,
  })
  const { data: manualExceptions } = useScreenTimeManualExceptionsQuery({
    userId: selectedUserId,
  })

  const currentExceptionEndDatetime = manualExceptions?.end_datetime || null

  const restrictionsOffMessage = useMemo(() => {
    if (!scheduledExceptions) return ""

    const groupedByTimeRange: Record<
      string,
      { timeRange: string; weekdays: string[] }
    > = {}

    for (const exception of scheduledExceptions) {
      const timeKey = `${exception.start_time}-${exception.end_time}`
      const timeRange = `${exception.start_time} - ${exception.end_time}`
      if (!groupedByTimeRange[timeKey]) {
        groupedByTimeRange[timeKey] = {
          timeRange,
          weekdays: [],
        }
      }
      groupedByTimeRange[timeKey].weekdays.push(exception.weekday)
    }

    // Sort weekdays within each time range group
    Object.values(groupedByTimeRange).forEach((group) => {
      group.weekdays.sort(
        (a, b) =>
          DAY_INDEX_MAP[a as keyof typeof DAY_INDEX_MAP] -
          DAY_INDEX_MAP[b as keyof typeof DAY_INDEX_MAP]
      )
    })

    const formattedGroups = Object.values(groupedByTimeRange)
      .sort((a, b) => {
        const aFirstDay = a.weekdays[0]
        const bFirstDay = b.weekdays[0]
        return (
          DAY_INDEX_MAP[aFirstDay as keyof typeof DAY_INDEX_MAP] -
          DAY_INDEX_MAP[bFirstDay as keyof typeof DAY_INDEX_MAP]
        )
      })
      .map(({ weekdays, timeRange }) => {
        const formattedWeekdays = weekdays
          .map((weekday) =>
            t(WEEKDAY_ABBR_KEYS[weekday as keyof typeof WEEKDAY_ABBR_KEYS])
          )
          .join(", ")
        return `${formattedWeekdays} ${timeRange}`
      })
      .join("\n")

    return `${t("STRS_OFF_ON_HEADER")}\n${formattedGroups}`
  }, [scheduledExceptions, t])

  const onClickToggleScreenTimeRestrictions = () => {
    onOpenToggleScreenTimeRestrictionsModal(currentExceptionEndDatetime)
  }

  const onClickScheduleException = () => {
    onOpenScreenTimeRestrictionsModal()
  }

  return (
    <div className="w-full flex flex-col justify-between p-3 bg-backgroundPrimary border border-borderPrimary rounded-md items-start gap-2">
      <div className="w-full flex flex-row gap-2 items-center justify-between">
        <div className="flex flex-row gap-1 items-center justify-start w-fit">
          {currentExceptionEndDatetime && (
            <FontAwesomeIcon
              icon={faHourglassHalf}
              color={JoonUIColor.icon.accent}
              fontSize={16}
            />
          )}
          <Typography variant="bodyBold">
            {currentExceptionEndDatetime
              ? t("STRS_RESTRICTION_OFF")
              : t("STRS_RESTRICTION_ON")}
          </Typography>
        </div>
        <Switch
          checked={!currentExceptionEndDatetime}
          onChange={onClickToggleScreenTimeRestrictions}
          name="screen-time-restrictions"
          variant="small"
        />
      </div>
      <div
        className={`flex flex-row gap-2 w-full ${
          !currentExceptionEndDatetime && !!scheduledExceptions?.length
            ? "border-t border-borderPrimary pt-2"
            : ""
        }`}
      >
        <Typography
          variant="bodySmall"
          color={JoonUIColor.text.secondary}
          className="whitespace-pre-line flex-1"
        >
          {currentExceptionEndDatetime
            ? t("STRS_RETURN_AT", {
                time: dayjs(currentExceptionEndDatetime).format("h:mm A"),
              })
            : !!scheduledExceptions?.length
              ? restrictionsOffMessage
              : t("STRS_WHEN_OFF_DESC", { name: child?.user.name })}
        </Typography>
        {!currentExceptionEndDatetime && !!scheduledExceptions?.length && (
          <SettingsGearButton onClick={onClickScheduleException} />
        )}
      </div>
      {!currentExceptionEndDatetime && !scheduledExceptions?.length && (
        <button
          className={`w-full flex justify-center p-2! border! border-borderPrimary! rounded-md items-center ${
            isHovered ? "bg-bgXLightGray!" : "bg-backgroundPrimary!"
          }`}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={onClickScheduleException}
        >
          <Typography variant="bodySmall">
            {t("STRS_SCHEDULE_EXCEPTION")}
          </Typography>
        </button>
      )}
    </div>
  )
}

export default ScreenTimeRestrictionsSection
